<template>
  <div class="home-view">
    <div class="container px-2">
      <!-- Carousel des articles vedettes -->
      <div class="featured-carousel">
        <v-carousel 
          height="400" 
          hide-delimiters 
          show-arrows="hover"
          cycle
          class="elevation-2 rounded-lg overflow-hidden"
        >
          <v-carousel-item
            v-for="(article, i) in featuredArticles"
            :key="i"
            @click="goToArticle(article._id)"
            class="cursor-pointer"
          >
            <v-img
              :src="getArticleImage(article, 'large')"
              height="400"
              cover
            ></v-img>
            <div class="carousel-content">
              <div class="carousel-category" :class="'category-' + article.category">
                {{ getCategoryName(article.category) }}
              </div>
              <h2 class="carousel-title">{{ article.title }}</h2>
              <div class="carousel-source d-flex align-center">
                <v-avatar size="24" class="mr-2" color="primary">
                  <v-img v-if="article.sourceId && article.sourceId.logo" :src="article.sourceId.logo" />
                  <v-icon v-else color="white">mdi-newspaper</v-icon>
                </v-avatar>
                <span>{{ article.sourceName }}</span>
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </div>
      
      <!-- Grids d'articles selon l'onglet sélectionné -->
      <div class="mt-8">
        <!-- Standard Mode -->
        <article-grid 
          v-if="activeTab === 'standard'"
          :gridItems="gridItems" 
          :loading="loading"
          mode="standard"
          class="mb-8"
        />
        
        <!-- Trending Mode - most viewed articles in last 5 days -->
        <article-grid 
          v-if="activeTab === 'trending'"
          :gridItems="trendingItems" 
          :loading="trendingLoading"
          mode="trending"
          class="mb-8"
        />
        
        <!-- Popular Mode - most liked articles in last 5 days -->
        <article-grid 
          v-if="activeTab === 'popular'"
          :gridItems="popularItems" 
          :loading="popularLoading"
          mode="popular"
          class="mb-8"
        />
        
        <!-- My Sources Mode -->
        <article-grid 
          v-if="activeTab === 'mySources' && isAuthenticated"
          :gridItems="mySourcesItems" 
          :loading="mySourcesLoading"
          mode="mySources"
          class="mb-8"
        />
        
        <!-- Message pour les utilisateurs non connectés essayant d'accéder à Mes Sources -->
        <v-card
          v-if="activeTab === 'mySources' && !isAuthenticated"
          class="text-center pa-8 mb-8"
        >
          <v-icon size="64" color="primary" class="mb-4">mdi-account-alert</v-icon>
          <h3 class="text-h5 mb-2">Connectez-vous pour voir vos sources</h3>
          <p class="text-body-1 text-medium-emphasis mb-4">
            Créez un compte ou connectez-vous pour personnaliser vos sources d'actualités
          </p>
          <div class="d-flex justify-center">
            <v-btn color="primary" class="mr-2" :to="{ name: 'Login' }">Se connecter</v-btn>
            <v-btn outlined :to="{ name: 'Register' }">S'inscrire</v-btn>
          </div>
        </v-card>
      </div>
      
      <!-- Bouton "Charger plus d'articles" -->
      <div class="text-center mb-8">
        <v-btn
          v-if="canLoadMore"
          color="primary"
          variant="outlined"
          @click="loadMoreArticles"
          :loading="loadingMore"
          :disabled="loadingMore"
          elevation="0"
          class="rounded-pill px-8"
        >
          <v-icon class="mr-2">mdi-refresh</v-icon>
          Charger plus d'articles
        </v-btn>
      </div>
      
      <!-- Indicateur de chargement pour l'infinite scroll -->
      <div v-if="loadingMore" class="text-center mb-8">
        <v-progress-circular
          indeterminate
          color="primary"
          size="32"
        ></v-progress-circular>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import ArticleGrid from '@/components/articles/ArticleGrid.vue';
import gridService from '@/services/gridService';
import appService from '@/services/appService';

export default {
  name: 'HomePage',
  components: {
    ArticleGrid
  },
  data() {
    return {
      // Données de la grid
      activeTab: 'standard',
      gridItems: [],
      trendingItems: [],
      popularItems: [],
      mySourcesItems: [],
      
      // États de chargement
      loading: true,
      trendingLoading: false,
      popularLoading: false,
      mySourcesLoading: false,
      loadingMore: false,
      
      // Pagination
      page: 1,
      canLoadMore: true,
      
      // Articles vedettes pour le carousel
      featuredArticles: []
    };
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/isAuthenticated',
      user: 'auth/user',
      storeActiveTab: 'ui/activeTab'
    }),
    
    // Obtient l'onglet actif depuis le store
    activeTab: {
      get() {
        return this.storeActiveTab;
      },
      set(value) {
        this.$store.dispatch('ui/setActiveTab', value);
      }
    },
    
    // Obtient le mode actif de la grille
    activeMode() {
      return this.activeTab;
    },
    
    // Obtient les éléments actuellement affichés
    currentItems() {
      switch (this.activeTab) {
        case 'trending':
          return this.trendingItems;
        case 'popular':
          return this.popularItems;
        case 'mySources':
          return this.mySourcesItems;
        default:
          return this.gridItems;
      }
    }
  },
  created() {
    // Charger les données initiales
    this.loadFeaturedArticles();
    this.loadGridItems();
    
    // Initialiser l'onglet par défaut
    const defaultTab = this.isAuthenticated ? 'standard' : 'standard';
    this.activeTab = defaultTab;
  },
  watch: {
    // Observer les changements d'onglet
    activeTab(newTab, oldTab) {
      if (newTab === oldTab) return;
      
      if (newTab === 'trending' && this.trendingItems.length === 0) {
        this.loadTrendingItems();
      } else if (newTab === 'popular' && this.popularItems.length === 0) {
        this.loadPopularItems();
      } else if (newTab === 'mySources' && this.mySourcesItems.length === 0) {
        this.loadMySourcesItems();
      }
    },
    
    // Observer l'état d'authentification
    isAuthenticated(newValue) {
      if (newValue && this.activeTab === 'mySources') {
        this.loadMySourcesItems();
      }
    }
  },
  methods: {
    // Gérer le changement d'onglet depuis le header
    handleTabChange(tab) {
      this.activeTab = tab;
      
      // Charger les données nécessaires pour l'onglet
      if (tab === 'trending' && this.trendingItems.length === 0) {
        this.loadTrendingItems();
      } else if (tab === 'popular' && this.popularItems.length === 0) {
        this.loadPopularItems();
      } else if (tab === 'mySources' && this.isAuthenticated && this.mySourcesItems.length === 0) {
        this.loadMySourcesItems();
      }
    },
    
    // Charger les articles vedettes pour le carousel
    async loadFeaturedArticles() {
      try {
        const response = await axios.get('/api/articles/featured');
        if (response.data.status === 'success') {
          // Prendre les articles tendance pour le carousel
          this.featuredArticles = response.data.data.trending.slice(0, 5);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des articles vedettes:', error);
        
        // Utiliser des données de secours en cas d'erreur
        this.featuredArticles = [
          {
            _id: '1',
            title: 'Le Premier ministre annonce un plan de relance économique de 100 milliards d\'euros',
            category: 'politique',
            sourceName: 'Le Monde',
            imageUrl: null
          },
          {
            _id: '2',
            title: 'Le président ukrainien en tournée européenne pour renforcer le soutien international',
            category: 'international',
            sourceName: 'Le Figaro',
            imageUrl: null
          },
          {
            _id: '3',
            title: 'Tour de France 2025 : le parcours dévoilé avec une étape inédite en montagne',
            category: 'sport',
            sourceName: 'L\'Équipe',
            imageUrl: null
          }
        ];
      }
    },
    
    // Obtenir l'image appropriée pour un article avec fallback
    getArticleImage(article, size = 'medium') {
      if (!article) return '';
      
      // Priorité aux images locales traitées
      if (size === 'large' && article.localImageLarge) {
        return article.localImageLarge;
      } else if (size === 'medium' && article.localImageMedium) {
        return article.localImageMedium;
      } else if (article.localImageSmall) {
        return article.localImageSmall;
      } else if (article.localImageUrl) {
        return article.localImageUrl;
      } else if (article.imageUrl) {
        return article.imageUrl;
      }
      
      // Fallback sur placeholder
      const id = article._id || Math.random().toString(36).substring(2, 15);
      const dimensions = {
        small: '400/225',
        medium: '800/450',
        large: '1200/675',
        xl: '1600/900'
      };
      
      return `https://picsum.photos/${dimensions[size] || '800/450'}?random=${id}`;
    },
    
    // Charger les articles de la grille principale (les plus récents avec filtrage des doublons)
    async loadGridItems() {
      this.loading = true;
      
      try {
        // Récupérer la grille depuis l'API
        const gridData = await gridService.fetchGrid({
          mode: 'standard',
          gridSize: gridService.config.initialLoad,
          filterDuplicates: true // Ajouter le paramètre pour filtrer les doublons
        });
        
        // Préparer les données pour les composants
        const preparedData = gridService.prepareGridForComponents(gridData);
        
        // Filtrer les articles avec des titres similaires
        // Note: Normalement ce filtrage devrait être fait côté backend,
        // mais nous l'ajoutons ici pour s'assurer qu'il est bien effectué
        this.gridItems = this.filterSimilarTitles(preparedData);
        
        // Réinitialiser la pagination
        this.page = 1;
        this.canLoadMore = this.gridItems.length >= gridService.config.initialLoad;
      } catch (error) {
        console.error('Erreur lors du chargement de la grille:', error);
        
        // Utiliser des données statiques en cas d'erreur
        this.generateStaticGridItems();
      } finally {
        this.loading = false;
      }
    },
    
    // Filtre les articles avec des titres similaires
    filterSimilarTitles(items) {
      // Garder uniquement les articles (ignorer les publicités, widgets, etc.)
      const articles = items.filter(item => item.type === 'article');
      const nonArticles = items.filter(item => item.type !== 'article');
      
      // Créer un ensemble pour suivre les titres similaires déjà vus
      const seenTitles = new Set();
      const filteredArticles = [];
      
      // Parcourir tous les articles et filtrer ceux avec des titres similaires
      for (const article of articles) {
        // Simplifier le titre pour la comparaison (supprimer la ponctuation, mettre en minuscules)
        const titleSimplified = article.content.title
          .toLowerCase()
          .replace(/[^\w\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        
        // Vérifier si un titre similaire existe déjà
        let isDuplicate = false;
        
        // Comparer avec les titres déjà vus
        for (const seenTitle of seenTitles) {
          // Calculer la similarité (ici on utilise une méthode simple, mais on pourrait
          // utiliser des algorithmes plus sophistiqués comme Levenshtein ou Jaccard)
          const similarity = this.calculateTitleSimilarity(seenTitle, titleSimplified);
          
          // Si la similarité dépasse un certain seuil, considérer comme doublon
          if (similarity > 0.7) {
            isDuplicate = true;
            break;
          }
        }
        
        // Si ce n'est pas un doublon, l'ajouter à notre liste filtrée et à l'ensemble des titres vus
        if (!isDuplicate) {
          filteredArticles.push(article);
          seenTitles.add(titleSimplified);
        }
      }
      
      // Combiner les articles filtrés avec les éléments non-articles
      return [...filteredArticles, ...nonArticles];
    },
    
    // Calcule la similarité entre deux chaînes (simplicité: nombre de mots communs / nombre total de mots uniques)
    calculateTitleSimilarity(title1, title2) {
      const words1 = title1.split(' ');
      const words2 = title2.split(' ');
      
      // Créer des ensembles pour compter les mots uniques
      const uniqueWords = new Set([...words1, ...words2]);
      const words1Set = new Set(words1);
      const words2Set = new Set(words2);
      
      // Compter les mots communs
      let commonWords = 0;
      for (const word of words1Set) {
        if (words2Set.has(word)) {
          commonWords++;
        }
      }
      
      // Calculer la similarité comme ratio des mots communs sur le total des mots uniques
      return commonWords / uniqueWords.size;
    },
    
    // Charger les articles tendance (plus vus des 5 derniers jours)
    async loadTrendingItems() {
      this.trendingLoading = true;
      
      try {
        // Récupérer la grille depuis l'API
        const gridData = await gridService.fetchGrid({
          mode: 'trending',
          gridSize: gridService.config.initialLoad,
          timeframe: '5d'  // Préciser les 5 derniers jours
        });
        
        // Préparer les données pour les composants
        this.trendingItems = gridService.prepareGridForComponents(gridData);
      } catch (error) {
        console.error('Erreur lors du chargement des articles tendance:', error);
        
        // Utiliser des données statiques en cas d'erreur
        this.generateStaticTrendingItems();
      } finally {
        this.trendingLoading = false;
      }
    },
    
    // Charger les articles populaires (plus likés des 5 derniers jours)
    async loadPopularItems() {
      this.popularLoading = true;
      
      try {
        // Récupérer la grille depuis l'API
        const gridData = await gridService.fetchGrid({
          mode: 'popular',
          gridSize: gridService.config.initialLoad,
          timeframe: '5d'  // Préciser les 5 derniers jours
        });
        
        // Préparer les données pour les composants
        this.popularItems = gridService.prepareGridForComponents(gridData);
      } catch (error) {
        console.error('Erreur lors du chargement des articles populaires:', error);
        
        // Utiliser des données statiques en cas d'erreur
        this.generateStaticPopularItems();
      } finally {
        this.popularLoading = false;
      }
    },
    
    // Charger les articles des sources suivies
    async loadMySourcesItems() {
      if (!this.isAuthenticated) return;
      
      this.mySourcesLoading = true;
      
      try {
        // Récupérer la grille depuis l'API
        const gridData = await gridService.fetchGrid({
          mode: 'mySources',
          gridSize: gridService.config.initialLoad
        });
        
        // Préparer les données pour les composants
        this.mySourcesItems = gridService.prepareGridForComponents(gridData);
      } catch (error) {
        console.error('Erreur lors du chargement des articles de mes sources:', error);
        
        // Utiliser des données statiques en cas d'erreur
        this.generateStaticMySourcesItems();
      } finally {
        this.mySourcesLoading = false;
      }
    },
    
    // Charger plus d'articles (pagination)
    async loadMoreArticles() {
      this.loadingMore = true;
      
      try {
        // Récupérer plus d'articles depuis l'API
        const result = await gridService.fetchMoreItems({
          mode: this.activeMode,
          page: this.page + 1
        });
        
        // Ajouter les nouveaux articles à la grille actuelle
        const newItems = gridService.prepareGridForComponents(result.items);
        
        // Mettre à jour la grille appropriée
        if (this.activeMode === 'trending') {
          this.trendingItems = [...this.trendingItems, ...newItems];
        } else if (this.activeMode === 'popular') {
          this.popularItems = [...this.popularItems, ...newItems];
        } else if (this.activeMode === 'mySources') {
          this.mySourcesItems = [...this.mySourcesItems, ...newItems];
        } else {
          this.gridItems = [...this.gridItems, ...newItems];
        }
        
        // Mettre à jour la pagination
        this.page++;
        this.canLoadMore = result.hasMore;
      } catch (error) {
        console.error('Erreur lors du chargement de plus d\'articles:', error);
        
        // Désactiver le chargement infini en cas d'erreur
        this.canLoadMore = false;
      } finally {
        this.loadingMore = false;
      }
    },
    
    
    // Navigation vers la page d'article
    goToArticle(id) {
      if (!id) return;
      
      this.$router.push(`/article/${id}`);
    },
    
    // Obtenir le nom de la catégorie
    getCategoryName(category) {
      const categories = {
        'politique': 'Politique',
        'economie': 'Économie',
        'technologie': 'Technologie',
        'sante': 'Santé',
        'sport': 'Sport',
        'culture': 'Culture',
        'international': 'International',
        'autres': 'Autres'
      };
      return categories[category] || 'Catégorie';
    },
    
    // Générer des données statiques en cas d'erreur (mode standard)
    generateStaticGridItems() {
      // Articles statiques avec différents types
      const staticArticles = Array(16).fill().map((_, index) => ({
        type: 'article',
        content: {
          _id: `static-${index}`,
          title: [
            'Le Premier ministre annonce un plan de relance économique',
            'Victoire historique en Coupe d\'Europe pour le club français',
            'Avancée scientifique majeure dans le domaine médical',
            'Les nouvelles mesures économiques détaillées en conseil des ministres',
            'Innovation technologique : ce qui va changer notre quotidien'
          ][index % 5],
          category: ['politique', 'economie', 'sport', 'sante', 'technologie', 'culture', 'international'][index % 7],
          sourceName: ['Le Monde', 'Le Figaro', 'L\'Équipe', 'Les Échos', 'Libération'][index % 5],
          imageUrl: null,
          publishedAt: new Date(Date.now() - (index * 3600000)).toISOString(),
          viewCount: Math.floor(Math.random() * 200)
        },
        subtype: index % 5 === 0 ? 'hot' : (index % 3 === 0 ? 'trending' : (index % 7 === 0 ? 'recommended' : null)),
        layout: index % 8 === 0 ? 'card-large' : (index % 4 === 0 ? 'card-medium' : 'card-small')
      }));
      
      // Ajouter des publicités
      const publicityItems = [
        {
          type: 'ad',
          content: {
            _id: 'ad-1',
            title: 'Offre spéciale',
            message: 'Découvrez nos nouveaux produits',
            imageUrl: null,
            targetUrl: 'https://example.com'
          },
          layout: 'card-small'
        }
      ];
      
      // Ajouter un widget météo
      const weatherItems = [
        {
          type: 'weather',
          content: null,
          layout: 'card-small'
        }
      ];
      
      // Combiner tous les éléments
      let allItems = [...staticArticles];
      
      // Insérer une publicité tous les 8 éléments
      for (let i = 7; i < allItems.length; i += 8) {
        allItems.splice(i, 0, publicityItems[0]);
      }
      
      // Insérer un widget météo en position 11
      if (allItems.length > 11) {
        allItems.splice(11, 0, weatherItems[0]);
      }
      
      this.gridItems = allItems;
    },
    
    // Générer des données statiques en cas d'erreur (mode trending - plus vus)
    generateStaticTrendingItems() {
      // Articles les plus vus des 5 derniers jours
      const trendingArticles = Array(16).fill().map((_, index) => ({
        type: 'article',
        content: {
          _id: `trending-${index}`,
          title: [
            'L\'actualité qui fait sensation sur les réseaux sociaux',
            'L\'information du jour qui fait débat',
            'Le sujet qui passionne les internautes aujourd\'hui',
            'La tendance qui ne cesse de monter en puissance',
            'Le phénomène viral qui secoue la toile'
          ][index % 5],
          category: ['politique', 'economie', 'sport', 'sante', 'technologie', 'culture', 'international'][index % 7],
          sourceName: ['Le Monde', 'Le Figaro', 'L\'Équipe', 'Les Échos', 'Libération'][index % 5],
          imageUrl: null,
          publishedAt: new Date(Date.now() - (index * 1800000)).toISOString(),
          viewCount: Math.floor(Math.random() * 500) + 100
        },
        subtype: index % 3 === 0 ? 'trending' : (index % 5 === 0 ? 'hot' : null),
        layout: index % 7 === 0 ? 'card-large' : (index % 3 === 0 ? 'card-medium' : 'card-small')
      }));
      
      this.trendingItems = trendingArticles;
    },
    
    // Générer des données statiques en cas d'erreur (mode popular - plus likés)
    generateStaticPopularItems() {
      // Articles les plus likés des 5 derniers jours
      const popularArticles = Array(16).fill().map((_, index) => ({
        type: 'article',
        content: {
          _id: `popular-${index}`,
          title: [
            'Article qui a suscité le plus d\'engagements cette semaine',
            'Contenu très apprécié par les lecteurs',
            'L\'article qui fait l\'unanimité chez nos lecteurs',
            'Information qui a reçu un accueil très positif',
            'Analyse saluée par la communauté'
          ][index % 5],
          category: ['politique', 'economie', 'sport', 'sante', 'technologie', 'culture', 'international'][index % 7],
          sourceName: ['Le Monde', 'Le Figaro', 'L\'Équipe', 'Les Échos', 'Libération'][index % 5],
          imageUrl: null,
          publishedAt: new Date(Date.now() - (index * 3600000)).toISOString(),
          viewCount: Math.floor(Math.random() * 200) + 50,
          likeCount: Math.floor(Math.random() * 300) + 100
        },
        subtype: index % 3 === 0 ? 'hot' : (index % 5 === 0 ? 'trending' : null),
        layout: index % 7 === 0 ? 'card-large' : (index % 3 === 0 ? 'card-medium' : 'card-small')
      }));
      
      this.popularItems = popularArticles;
    },
    
    // Générer des données statiques en cas d'erreur (mode mes sources)
    generateStaticMySourcesItems() {
      const mySourcesArticles = Array(12).fill().map((_, index) => ({
        type: 'article',
        content: {
          _id: `mysource-${index}`,
          title: [
            'Article exclusif de votre source préférée',
            'L\'information ciblée qui vous intéresse',
            'Contenu personnalisé selon vos préférences',
            'L\'actualité qui correspond à vos centres d\'intérêt',
            'Les dernières nouvelles de votre domaine favori'
          ][index % 5],
          category: ['politique', 'economie', 'sport', 'sante', 'technologie'][index % 5],
          sourceName: ['Le Monde', 'Le Figaro', 'L\'Équipe'][index % 3],
          imageUrl: null,
          publishedAt: new Date(Date.now() - (index * 7200000)).toISOString(),
          viewCount: Math.floor(Math.random() * 100)
        },
        subtype: 'recommended',
        layout: index % 6 === 0 ? 'card-large' : (index % 3 === 0 ? 'card-medium' : 'card-small')
      }));
      
      this.mySourcesItems = mySourcesArticles;
    }
  }
};
</script>

<style scoped>
.carousel-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  background: linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0));
  color: white;
  cursor: pointer;
}

.v-carousel-item {
  cursor: pointer;
}

.carousel-category {
  display: inline-block;
  background-color: #2196F3;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.category-politique { background-color: #F44336; }
.category-economie { background-color: #4CAF50; }
.category-sport { background-color: #FFC107; color: rgba(0, 0, 0, 0.87); }
.category-international { background-color: #2196F3; }
.category-technologie { background-color: #9C27B0; }
.category-culture { background-color: #FF9800; }
.category-sante { background-color: #00BCD4; }

.carousel-title {
  font-size: 1.75rem;
  font-weight: 500;
  margin-bottom: 16px;
  color: white;
  text-shadow: 0 1px 3px rgba(0,0,0,0.5);
}
</style>